import {
  isAssessTemplatedReportForCommentsStep,
  isChoiceStep,
} from "#src/batteries-included-components/Drawers/Workflows/WorkflowConfigurationDrawer/WorkflowConfigurationAddNodeDrawer.helpers";
import {
  AssessTemplatedReportForCommentsAction,
  ChoiceTask,
  FormChoiceAction,
  VariableChoiceAction,
  WorkflowConfigStepType,
  WorkflowParallelTasksType,
  WorkflowStepType,
} from "@validereinc/domain";
import { cloneDeep } from "es-toolkit/compat";
import { nanoid } from "nanoid";

/**
 * Recursively collects all step IDs (including nested parallel steps) and assigns new IDs.
 * Updates the shared idMap to ensure all IDs are unique.
 * Uses nanoid to generate new IDs, preserving the prefix pattern from the original ID.
 */
const collectAndMapIds = (
  stepRecords: Record<string, WorkflowConfigStepType>,
  idMap: Record<string, string>
) => {
  Object.keys(stepRecords).forEach((oldId) => {
    if (idMap[oldId]) {
      // Already mapped, skip
      return;
    }

    // Extract the prefix pattern from the original ID
    // For IDs like "branch-step-abc12def" or "manual-step-xyz789", extract "branch-step" or "manual-step"
    // For IDs like "branch-step-5" (legacy numeric), extract "branch-step"
    const parts = oldId.split("-");
    let prefix: string;

    if (parts.length > 1) {
      // Extract everything except the last part as the prefix
      // This handles both nanoid-based IDs (e.g., "branch-step-abc12def") and numeric IDs (e.g., "branch-step-5")
      prefix = parts.slice(0, -1).join("-");
    } else {
      // Fallback: if there's only one part, use it as the prefix
      prefix = oldId;
    }

    // Generate a new ID using nanoid with the same prefix pattern
    const newId = `${prefix}-${nanoid(8)}`;
    idMap[oldId] = newId;

    // If this step is a parallel step, recursively collect IDs from its branches
    const step = stepRecords[oldId];
    if (step.type === WorkflowStepType.PARALLEL) {
      step.branches.forEach((branch) => {
        collectAndMapIds(branch.steps, idMap);
      });
    }
  });
};

/**
 * Recursively remaps step IDs in a set of steps, including nested parallel steps.
 * Assumes all IDs have already been collected into idMap.
 * Updates external next references (not in idMap) to point to nextStepId if provided and valid.
 */
const remapStepsRecursively = (
  steps: Record<string, WorkflowConfigStepType>,
  idMap: Record<string, string>,
  nextStepId: string | undefined,
  isValidNextStepId: boolean
): Record<string, WorkflowConfigStepType> => {
  const newSteps: Record<string, WorkflowConfigStepType> = {};

  Object.entries(steps).forEach(([oldStepId, step]) => {
    const newStepId = idMap[oldStepId];
    const newStep = cloneDeep(step);

    if (newStep.next && idMap[newStep.next]) {
      // Remap internal reference within the parallel structure
      newStep.next = idMap[newStep.next];
    } else if (newStep.next && !idMap[newStep.next]) {
      // External reference - steps inside parallel branches should end here
      // The parallel step's next property handles convergence to the next step
      newStep.next = undefined;
      newStep.end = true;
    }
    // If step has no next, keep end flag as is (steps inside parallel branches
    // that already have end = true should remain that way)

    if (newStep.type === WorkflowStepType.USER_TASK) {
      newStep.assignee = newStep.assignee ?? null;
      newStep.assignee_user = newStep.assignee_user ?? null;
      newStep.assignee_group = newStep.assignee_group ?? null;
    }

    if (isChoiceStep(newStep)) {
      const choiceTask = newStep.task as
        | ChoiceTask
        | FormChoiceAction
        | VariableChoiceAction;
      choiceTask.choices = choiceTask.choices.map((choice) => {
        if (choice.next && idMap[choice.next]) {
          // Remap internal reference within the parallel structure
          return { ...choice, next: idMap[choice.next] };
        } else if (choice.next && !idMap[choice.next]) {
          // External reference - should end here
          return { ...choice, next: undefined, end: true };
        }
        return choice;
      });
    }

    // Remap assess templated report step assessment_success_next and assessment_failure_next
    if (isAssessTemplatedReportForCommentsStep(newStep)) {
      const assessAction =
        newStep.task as AssessTemplatedReportForCommentsAction;
      if (
        assessAction.assessment_success_next &&
        idMap[assessAction.assessment_success_next]
      ) {
        // Remap internal reference within the parallel structure
        assessAction.assessment_success_next =
          idMap[assessAction.assessment_success_next];
      } else if (
        assessAction.assessment_success_next &&
        !idMap[assessAction.assessment_success_next]
      ) {
        // External reference - point to the parallel step's next if available
        // This ensures consistency with how other external references are handled
        if (isValidNextStepId && nextStepId) {
          assessAction.assessment_success_next = nextStepId;
        }
        // If nextStepId is not valid, keep the original value (though this may create broken references)
      }
      if (
        assessAction.assessment_failure_next &&
        idMap[assessAction.assessment_failure_next]
      ) {
        // Remap internal reference within the parallel structure
        assessAction.assessment_failure_next =
          idMap[assessAction.assessment_failure_next];
      } else if (
        assessAction.assessment_failure_next &&
        !idMap[assessAction.assessment_failure_next]
      ) {
        // External reference - point to the parallel step's next if available
        // This ensures consistency with how other external references are handled
        if (isValidNextStepId && nextStepId) {
          assessAction.assessment_failure_next = nextStepId;
        }
        // If nextStepId is not valid, keep the original value (though this may create broken references)
      }
    }

    // If this step is a parallel step, recursively remap its nested branches
    if (newStep.type === WorkflowStepType.PARALLEL) {
      newStep.branches = newStep.branches.map((branch) => ({
        ...branch,
        start: idMap[branch.start] ?? branch.start,
        steps: remapStepsRecursively(
          branch.steps,
          idMap,
          nextStepId,
          isValidNextStepId
        ),
      }));
    }

    newSteps[newStepId] = newStep;
  });

  return newSteps;
};

/**
 * Remaps IDs for parallel branches in a cloned step.
 * This ensures all step IDs within parallel branches are unique and properly remapped.
 */
export const remapIdsForParallelBranches = (
  clonedStepToInsert: WorkflowParallelTasksType,
  nextStepId: string | undefined,
  isValidNextStepId: boolean
) => {
  const idMap: Record<string, string> = {};

  // First pass: collect all IDs from all branches (including nested parallel steps)
  clonedStepToInsert.branches.forEach((branch) => {
    collectAndMapIds(branch.steps, idMap);
  });

  // Second pass: remap all branches using the collected idMap
  const remapBranch = (
    branch: WorkflowParallelTasksType["branches"][number]
  ) => {
    return {
      ...branch,
      start: idMap[branch.start] ?? branch.start,
      steps: remapStepsRecursively(
        branch.steps,
        idMap,
        nextStepId,
        isValidNextStepId
      ),
    };
  };

  clonedStepToInsert.branches = clonedStepToInsert.branches.map(remapBranch);

  // Set the parallel step's next to point to the insertion context's next step
  // This ensures all branches converge to the correct next step
  // Only set next if nextStepId is a valid step ID
  if (isValidNextStepId && nextStepId) {
    clonedStepToInsert.next = nextStepId;
    clonedStepToInsert.end = false;
  } else {
    // If nextStepId is not valid (e.g., a visual -end node), set end = true
    clonedStepToInsert.end = true;
  }
};
